import React, {useState, useRef, useEffect} from "react";
import {useForm} from "react-hook-form";
import {AlertCircle, Barcode} from "lucide-react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import FormHeader from "../../Common/FormHeader";
import ColorPalette from "../ColorPalette";
import IOSSwitch from "../../Common/IOSSwitch";
import {MdClose} from 'react-icons/md';
import secureStorage from "../../../utils/secureStorage";
import {generateVariantBarcode} from "../../../utils/barcodeGenerator";
import {api} from "../../../config/api";
import AppleSelect from "../../Common/AppleSelect";
import Notification from "../../Common/Notification";

const NewVarianteProductForm = () => {
    const {productId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Back to the parent product, passing on where its own "Retour" leads
    const backToProduct = () => navigate(`/catalogue/product/edit/${productId}`, {state: {returnTo: location.state?.returnTo}});
    const [currentColor, setCurrentColor] = useState("#4DB6AC");
    const [showPalette, setShowPalette] = useState(false);
    const [variantImage, setVariantImage] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [isStockable, setIsStockable] = useState(true);
    const [isVendable, setIsVendable] = useState(true);
    const [parentProduct, setParentProduct] = useState(null);
    const [units, setUnits] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [notification, setNotification] = useState({
        show: false,
        type: 'success',
        message: '',
    });

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
    } = useForm();

    const formRef = useRef(null);

    // Function to show a notification
    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message,
        });

        // Auto hide after 5 seconds for success notifications
        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 5000);
        }
    };

    // Function to hide notification
    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    // Fetch parent product data and units on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                const headers = {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                };

                const [productRes, unitsRes, taxesRes] = await Promise.all([
                    api.get(`/products/${productId}`, {headers}),
                    api.get('/units', {headers}),
                    api.get('/taxes', {headers})
                ]);

                setParentProduct(productRes.data);

                const unitsData = unitsRes.data;
                setUnits(Array.isArray(unitsData) ? unitsData : unitsData['hydra:member'] || unitsData.data || []);

                const taxesData = taxesRes.data;
                setTaxes(Array.isArray(taxesData) ? taxesData : taxesData['hydra:member'] || taxesData.items || []);

                // Set initial values from parent product
                setValue("unitsId", productRes.data.units?.id || "");
                setValue("taxId", productRes.data.taxes?.id || "");
                setIsStockable(productRes.data.isStockable || true);
                setIsVendable(productRes.data.isVendable || true);
            } catch (error) {
                showNotification('error', "Erreur lors du chargement des données");
            }
        };

        fetchData();
    }, [productId, setValue]);

    const onSubmit = async (data) => {
        try {
            showNotification('loading', "Création de la déclinaison en cours...");

            // Create FormData object
            const formData = new FormData();

            // Add necessary fields with proper values
            formData.append("name", data.name);
            formData.append("color", currentColor);
            formData.append("description", data.description || "");
            formData.append("price", data.price);
            formData.append("barcode", data.barcode || "");
            formData.append("product", `/api/products/${productId}`);
            formData.append("units", `/api/units/${data.unitsId}`);
            formData.append("taxe", `/api/taxes/${data.taxId}`);  // field is `taxe` (singular) on ProductVariant
            formData.append("active", isActive);
            formData.append("isStockable", isStockable);
            formData.append("isVendable", isVendable);

            // Handle image upload
            if (variantImage) {
                if (variantImage instanceof File) {
                    formData.append("image", variantImage);
                } else if (typeof variantImage === 'string' && variantImage.startsWith('data:')) {
                    const response = await fetch(variantImage);
                    const blob = await response.blob();
                    const file = new File([blob], "variant-image.jpg", {type: "image/jpeg"});
                    formData.append("image", file);
                }
            }

            // Get account ID
            const accountId = await secureStorage.getItem("selectedAccount");

            // Create variant using the API
            const response = await api.post('/variants', formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Content-Type': undefined,
                    'Accept': 'application/json'
                },
                maxBodyLength: Infinity
            });

            showNotification('success', "Déclinaison créée avec succès");

            // Give time for notification to be seen
            setTimeout(() => {
                backToProduct();
            }, 1500);
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        }
    };

    const handleColorChange = (color) => {
        setCurrentColor(color);
        setShowPalette(false);
    };

    const handleImageChange = (image) => {
        setVariantImage(image);
    };

    const togglePalette = () => {
        setShowPalette((prev) => !prev);
    };

    const toggleActive = () => {
        setIsActive((prev) => !prev);
    };

    const toggleStockable = () => {
        setIsStockable((prev) => !prev);
    };

    const toggleVendable = () => {
        setIsVendable((prev) => !prev);
    };

    const handleDeleteImage = () => {
        setVariantImage(null);
    };

    // Generate a barcode via the API
    const generateBarcode = async () => {
        try {
            const barcode = await generateVariantBarcode();
            setValue("barcode", barcode);
            showNotification('success', "Code-barres généré avec succès");
        } catch (e) {
            console.error("Erreur lors de la génération du code-barres", e);
            showNotification('error', "Erreur lors de la génération du code-barres");
        }
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 product-form-container">
            {/* Enhanced Responsive Header */}
            <FormHeader title={parentProduct ? `Nouvelle déclinaison — ${parentProduct.name}` : "Nouvelle déclinaison"} back={backToProduct} onSave={() => formRef.current && handleSubmit(onSubmit)()}/>

            {/* Enhanced Responsive Form */}
            <form
                ref={formRef}
                className="form-content w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8"
                onSubmit={handleSubmit(onSubmit)}
            >
                {/* Enhanced Image/Color Section */}
                <div className="image-color-section flex flex-col items-center space-y-3 sm:space-y-4 p-4 sm:p-6">
                    <div className="text-center">
                        {variantImage ? (
                            <div className="image-container relative w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 mx-auto">
                                <img
                                    src={variantImage}
                                    alt="Variant"
                                    className="w-full h-full object-cover rounded-xl shadow-lg cursor-pointer transition-transform hover:scale-105 active:scale-95"
                                    onClick={togglePalette}
                                />
                                <button
                                    type="button"
                                    onClick={handleDeleteImage}
                                    className="delete-button absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-red-500 text-white rounded-full p-1.5 sm:p-2 shadow-lg hover:bg-red-600 transition-all duration-200 min-w-[28px] min-h-[28px] sm:min-w-[32px] sm:min-h-[32px] flex items-center justify-center"
                                    aria-label="Supprimer l'image"
                                >
                                    <MdClose className="w-3 h-3 sm:w-4 sm:h-4"/>
                                </button>
                            </div>
                        ) : (
                            <div
                                className="image-container w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 rounded-xl flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl transition-all duration-200 mx-auto text-white font-medium text-sm sm:text-base"
                                onClick={togglePalette}
                                style={{backgroundColor: currentColor}}
                            >
                                <span className="select-none">Éditer</span>
                            </div>
                        )}
                        <p className="edit-label text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2 sm:mt-3">
                            Cliquez pour modifier
                        </p>
                    </div>
                </div>

                {/* Enhanced Form Fields Section */}
                <div className="form-fields space-y-4 sm:space-y-6">
                    {/* Name Field */}
                    <div className="field-group">
                        <label htmlFor="name" className="field-label text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                            Nom de la déclinaison <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            autoComplete="off"
                            {...register("name", {
                                required: "Le champ 'Nom' est obligatoire.",
                            })}
                            placeholder="Nom de la déclinaison*"
                            className={`form-input w-full px-3 sm:px-4 py-2.5 sm:py-3 border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                                errors.name
                                    ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                    : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                            }`}
                        />
                        {errors.name && (
                            <p className="error-message text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertCircle className="w-4 h-4 flex-shrink-0"/>
                                {errors.name.message}
                            </p>
                        )}
                    </div>

                    {/* Divider */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="space-y-4 sm:space-y-6">
                            {/* Description Field */}
                            <div className="field-group">
                                <label htmlFor="description" className="field-label text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                                    Description (optionnelle)
                                </label>
                                <textarea
                                    id="description"
                                    {...register("description")}
                                    rows="3"
                                    placeholder="Description de la déclinaison"
                                    className="form-textarea w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-vertical transition-all duration-200"
                                />
                            </div>

                            {/* Price and Barcode Grid */}
                            <div className="field-grid grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                <div className="field-group">
                                    <label htmlFor="price" className="field-label text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                                        Prix <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        id="price"
                                        {...register("price", {
                                            required: "Le prix est obligatoire.",
                                            min: {value: 0, message: "Le prix doit être positif"}
                                        })}
                                        placeholder="Prix*"
                                        className={`form-input w-full px-3 sm:px-4 py-2.5 sm:py-3 border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                                            errors.price
                                                ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                                : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                        }`}
                                    />
                                    {errors.price && (
                                        <p className="error-message text-red-500 text-sm mt-1 flex items-center gap-1">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0"/>
                                            {errors.price.message}
                                        </p>
                                    )}
                                </div>

                                <div className="field-group">
                                    <label htmlFor="barcode" className="field-label text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                                        Code à barres
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id="barcode"
                                            {...register("barcode")}
                                            placeholder="Code à barres"
                                            className="form-input w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                                        />
                                        <button
                                            type="button"
                                            onClick={generateBarcode}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-primary hover:text-primary-dark transition-colors"
                                            title="Générer un code-barres"
                                        >
                                            <Barcode className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Cliquez sur l'icône pour générer automatiquement
                                    </p>
                                </div>
                            </div>

                            {/* Units Dropdown */}
                            <div className="field-group">
                                <label htmlFor="unitsId" className="field-label text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                                    Unité <span className="text-red-500">*</span>
                                </label>
                                <AppleSelect
                                    id="unitsId"
                                    {...register("unitsId", {
                                        required: "L'unité est obligatoire."
                                    })}
                                    className={`form-select w-full px-3 sm:px-4 py-2.5 sm:py-3 border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                                        errors.unitsId
                                            ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                            : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                    }`}
                                >
                                    <option value="">Sélectionner une unité</option>
                                    {Array.isArray(units) && units.map(unit => (
                                        <option key={unit.id} value={unit.id}>
                                            {unit.name} ({unit.code})
                                        </option>
                                    ))}
                                </AppleSelect>
                                {errors.unitsId && (
                                    <p className="error-message text-red-500 text-sm mt-1 flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0"/>
                                        {errors.unitsId.message}
                                    </p>
                                )}
                            </div>

                            {/* Tax Dropdown */}
                            <div className="field-group">
                                <label htmlFor="taxId" className="field-label text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                                    Taux de TVA <span className="text-red-500">*</span>
                                </label>
                                <AppleSelect
                                    id="taxId"
                                    {...register("taxId", {
                                        required: "Le taux de TVA est obligatoire."
                                    })}
                                    className={`form-select w-full px-3 sm:px-4 py-2.5 sm:py-3 border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                                        errors.taxId
                                            ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                            : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                    }`}
                                >
                                    <option value="">Sélectionner un taux de TVA</option>
                                    {Array.isArray(taxes) && taxes.map(tax => (
                                        <option key={tax.id} value={tax.id}>
                                            {tax.value}% ({tax.name || ''})
                                        </option>
                                    ))}
                                </AppleSelect>
                                {errors.taxId && (
                                    <p className="error-message text-red-500 text-sm mt-1 flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0"/>
                                        {errors.taxId.message}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Switch Controls */}
                    <div className="switch-controls flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 lg:gap-6 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 sm:p-6">
                        <div className="switch-item flex items-center justify-between sm:justify-start sm:gap-3 min-h-[44px] p-3 sm:p-2 bg-white dark:bg-gray-700 sm:bg-transparent dark:sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-gray-200 dark:border-gray-600">
                            <span className="switch-label text-sm font-medium text-gray-700 dark:text-gray-300">
                                Actif
                            </span>
                            <IOSSwitch
                                checked={isActive}
                                onChange={toggleActive}
                            />
                        </div>

                        <div className="switch-item flex items-center justify-between sm:justify-start sm:gap-3 min-h-[44px] p-3 sm:p-2 bg-white dark:bg-gray-700 sm:bg-transparent dark:sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-gray-200 dark:border-gray-600">
                            <span className="switch-label text-sm font-medium text-gray-700 dark:text-gray-300">
                                Stockable
                            </span>
                            <IOSSwitch
                                checked={isStockable}
                                onChange={toggleStockable}
                            />
                        </div>

                        <div className="switch-item flex items-center justify-between sm:justify-start sm:gap-3 min-h-[44px] p-3 sm:p-2 bg-white dark:bg-gray-700 sm:bg-transparent dark:sm:bg-transparent rounded-lg sm:rounded-none border sm:border-0 border-gray-200 dark:border-gray-600">
                            <span className="switch-label text-sm font-medium text-gray-700 dark:text-gray-300">
                                Vendable
                            </span>
                            <IOSSwitch
                                checked={isVendable}
                                onChange={toggleVendable}
                            />
                        </div>
                    </div>
                </div>
            </form>

            {/* Enhanced Color Palette Modal */}
            {showPalette && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={togglePalette}/>
                    <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
                        <div className="relative w-full sm:max-w-2xl bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[85vh] sm:h-auto sm:max-h-[85vh]"
                             onClick={e => e.stopPropagation()}>
                            <ColorPalette
                                initialColor={currentColor}
                                onClose={togglePalette}
                                onColorChange={handleColorChange}
                                onImageChange={handleImageChange}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Enhanced Notification */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />
        </div>
    );
};

export default NewVarianteProductForm;
